import { Layout } from '@components/layout/Layout';
import {
  UserRoles,
  UserStatus,
  UserOrganizationRoleStatus,
} from '@models/user.model';
import { canAccess } from '@utils/featureFlag';
import { isMobileOnlyRole, isExternalViewerRole } from '@constants/roleHierarchy';
import AuthPage from '@pages/auth';
import LoginPage from '@pages/login';
import RegisterPage from '@pages/register/register';
import LogoutPage from '@pages/logout';
import OrganizationsPage from '@pages/organizations/organizations';
import SettingsPage from '@pages/settings';
import AdminPage from '@pages/admin';
import SchedulePage from '@pages/schedule';
import React, { useEffect, useRef, useState } from 'react';
import {
  Redirect,
  Route,
  RouteProps,
  BrowserRouter as Router,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { useAppSelector, useAppDispatch } from './app/redux/hooks';
import { useSyncHtmlLang } from '@hooks/useSyncHtmlLang';
import { RootState } from './app/redux/store';
import { setUserDetails } from './app/redux/reducers/user.reducer';
import { useCurrentUser } from '@hooks/api';
import { OrganizationStatus } from '@models/organization.model';
import userService from '@services/user';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import {
  setOrganizationIdLocalStorage,
  setOrganizationIdSessionStorage,
} from '@config/storage';
import { useOrganization } from '@hooks/api/organization/useOrganization';
import DashboardPage from '@pages/dashboard';
import ClientsPage from '@pages/clients';
import ProjectsPage from '@pages/projects';
import ProjectWizardPage from '@pages/projects/ProjectWizardPage';
import AiPlanPage from '@pages/projects/AiPlanPage';
import CrewsPage from '@pages/crews';
import SubcontractorsPage from '@pages/subcontractors/subcontractors';
import BudgetsPage from '@pages/budgets';
import InvoicesPage from '@pages/invoices';
import DocumentsPage from '@pages/documents';
import TemplatesPage from '@pages/templates';
import IncidentsPage from '@pages/incidents';
import PlatformStatusAdminPage from '@pages/admin/platformStatus';
import WarehousePage from '@pages/warehouse';
import MaterialCatalogPage from '@pages/warehouse/materials';
import TransactionsPage from '@pages/warehouse/transactions';
import LowStockAlertsPage from '@pages/warehouse/alerts';
import EquipmentAssetsPage from '@pages/warehouse/equipment';
import EquipmentAssetDetailPage from '@pages/warehouse/equipment-detail';
import InventoryValuationReportPage from '@pages/warehouse/reports-valuation';
import WarehouseCategoriesPage from '@pages/warehouse/categories';
import WarehouseManagementPage from '@pages/warehouse/warehouses';
import SuperAdminCategoriesPage from '@pages/admin/warehouse/categories';
import GeofencesPage from '@pages/geofences';
import GeofenceCategoriesPage from '@pages/geofences/categories';
import LogRocket from 'logrocket';
import { markLogRocketInitialized, isLogRocketInitialized } from '@config/logRocketStatus';
import { requestSanitizer, responseSanitizer } from '@config/logRocketSanitizer';
import { initAnalytics, sanitizePageLocation, stripNonUtmQuery, trackPageView } from '@config/analytics';
import HomePage from '@pages/home';
import HomeLayout from '@components/layout/HomeLayout';
import MapViewPage from '@pages/mapView';
import { UiComponents } from '@pages/uiComponents';
import UsersPage from '@pages/users';
import UserDisabled from '@pages/userDisabled/userDisabled';
import OrganizationDisabled from '@pages/organizationDisabled';
import NoOrganization from '@pages/noOrganization';
import OrganizationSuspended from '@pages/organizationSuspended';
import ProfilePage from '@pages/profile';
import MobileOnly from '@pages/mobileOnly';
import NotFoundPage from '@pages/notFound';
import KnownPathRedirect from './app/routes/KnownPathRedirect';
import { KNOWN_APP_PATH_PREFIXES } from './app/routes/knownAppPaths';
import OwnerBillingPage from '@pages/ownerBilling';
import TrialExpiredPage from '@pages/trialExpired';
import AdminBillingPage from '@pages/adminBilling';
import SubscriptionPlansAdminPage from '@pages/subscriptionPlansAdmin';
import SuperAdminNotificationRecipientsPage from '@pages/superAdminNotificationRecipients';
import StandaloneInvoicePage from '@pages/admin/standaloneInvoice';
import FieldCaptureAdminPage from '@pages/fieldCaptureAdmin';
import DailySiteReportsPage from '@pages/dailySiteReports/list';
import AssistantPage from '@pages/assistant';
import DailySiteReportDetailPage from '@pages/dailySiteReports/detail';

interface PrivateRouteProps extends RouteProps {
  component: React.ComponentType<any>;
}

// Landing route after login, by role. REVIEWER is web-only and read-only; it can
// reach Projects and the read-only Field Capture tab, so it lands on Projects
// rather than the (inaccessible) dashboard.
const getPostLoginRedirect = (role: string | null): string => {
  if (role === UserRoles.SUPER_ADMIN) return '/organizations';
  if (isMobileOnlyRole(role)) return '/home';
  // Sprint 25, story-128. CLIENT joins REVIEWER — same landing, same reasoning.
  if (role === UserRoles.REVIEWER || role === UserRoles.CLIENT) return '/projects';
  return '/dashboard';
};

// Sprint 25, story-127 (web sweep #1). Most routes below render unconditionally regardless of
// role — only a handful gate themselves with `canAccess(...)` at the <Switch> level. Without a
// generic guard, a REVIEWER/CLIENT that types (or deep-links to) e.g. /budgets or /warehouse
// reaches the page component directly, which then either errors against the story-126 server
// block or, worse, renders whatever it can before a write fails. `/projects` and `/profile` are
// the two routes both external-viewer roles are designed to reach; `/admin/field-capture` is the
// one further exception, REVIEWER-only, already gated by its own `canAccess('FIELD_CAPTURE_ADMIN')`
// check at the <Switch> level, so it never mounts this component for CLIENT in the first place —
// re-checked here anyway so a REVIEWER landing on it directly is allowed through, not redirected.
const EXTERNAL_VIEWER_ALLOWED_PATH_PREFIXES = ['/projects', '/profile', '/reports'];

const isPathAllowedForExternalViewer = (pathname: string, role: string | null): boolean => {
  if (EXTERNAL_VIEWER_ALLOWED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return true;
  }
  return pathname.startsWith('/admin/field-capture') && canAccess('FIELD_CAPTURE_ADMIN', role);
};

const App: React.FC = () => {
  const { isLoggedIn, role, userId, email } = useAppSelector(
    (state: RootState) => state.user,
  );

  // Sprint 27, story-139: keeps <html lang> synced with the active locale on every change.
  useSyncHtmlLang();

  const userInBlacklist = (email: string | null | undefined) => {
    const blackList: string[] = [
      'edwin@getzenhammer.com',
    ]
    if (process.env.NODE_ENV === 'production') {
      return !!email && (blackList.includes(email) || email.indexOf("@maxplanck.dev") !== -1)
    }
    return !!email && blackList.includes(email);
  }
  if (
    window.location.hostname !== 'localhost' &&
    process.env.REACT_APP_LOGROCKET_ID &&
    !userInBlacklist(email)
  ) {
    if (userId && email && role && !isLogRocketInitialized()) {
      LogRocket.init(process.env.REACT_APP_LOGROCKET_ID, {
        network: {
          requestSanitizer,
          responseSanitizer,
        },
        dom: {
          // Masked by default, not opt-in per field — no `data-lr-unmask`-style opt-out is
          // added by this story, only the capability for one later.
          inputSanitizer: true,
        },
      });
      LogRocket.identify(userId, {
        email: email,
        role: role,
        environment: window.location.hostname,
      });
      markLogRocketInitialized();
    }
  } else {
    console.log('LogRocket is not initialized');
  }

  const PrivateRoute: React.FC<PrivateRouteProps> = ({
    component: Component,
    ...rest
  }) =>
    // Mobile-only roles get no web route but /home, deep links included. Checked
    // before <Layout> so the office shell never mounts or fetches for them.
    isLoggedIn && isMobileOnlyRole(role) ? (
      <Route {...rest} render={() => <Redirect to="/home" />} />
    ) : (
      <Layout>
        <Route
          {...rest}
          render={(props) =>
            isLoggedIn ? (
              isExternalViewerRole(role) &&
              !isPathAllowedForExternalViewer(props.location.pathname, role) ? (
                <Redirect to="/projects" />
              ) : (
                <Component {...props} />
              )
            ) : (
              (() => {
                if (window.location.pathname !== '/logout') {
                  sessionStorage.setItem(
                    'redirectUrl',
                    window.location.pathname + window.location.search,
                  );
                }
                return <Redirect to="/auth" />;
              })()
            )
          }
        />
      </Layout>
    );

  const PrivateHomeRoute: React.FC<PrivateRouteProps> = ({
    component: Component,
    ...rest
  }) => (
    <HomeLayout>
      <Route
        {...rest}
        render={(props) =>
          isLoggedIn ? (
            <Component {...props} />
          ) : (
            (() => {
              if (window.location.pathname !== '/logout') {
                sessionStorage.setItem(
                  'redirectUrl',
                  window.location.pathname + window.location.search,
                );
              }
              return <Redirect to="/auth" />;
            })()
          )
        }
      />
    </HomeLayout>
  );

  const StatusChecker: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const reduxStatus = useAppSelector((state: RootState) => state.user.status);
    const reduxRole = useAppSelector((state: RootState) => state.user.role);
    const reduxIsReadOnly = useAppSelector(
      (state: RootState) => state.user.isReadOnly,
    );
    const organizationId = useAppSelector(
      (state: RootState) => state.organization.organizationId,
    );
    const [hasCheckedForSwitch, setHasCheckedForSwitch] = useState(false);

    // Only fetch user data if user has an organization
    const { data: currentUserData } = useCurrentUser({
      enabled: isLoggedIn && !!organizationId,
    });

    useEffect(() => {
      const checkAndHandleDisabledStatus = async () => {
        if (!isLoggedIn || !currentUserData?.getSelf) return;

        const userStatus = currentUserData.getSelf.status;
        const userRole = currentUserData.getSelf.role;
        const organizationRoleStatus =
          currentUserData.getSelf.organizationRoleStatus;

        // Only update Redux if status has changed to avoid infinite loops
        if (userStatus && userStatus !== reduxStatus) {
          dispatch(setUserDetails({ status: userStatus }));
        }

        // Update role in Redux if it has changed (important for multi-org users)
        if (userRole && userRole !== reduxRole) {
          dispatch(setUserDetails({ role: userRole }));
        }

        // Update isReadOnly in Redux based on organizationRoleStatus
        const isReadOnly =
          organizationRoleStatus === UserOrganizationRoleStatus.READ_ONLY;
        if (isReadOnly !== reduxIsReadOnly) {
          dispatch(setUserDetails({ isReadOnly }));
        }

        // Check if user is DISABLED in the current organization
        const isUserDisabled = userStatus === UserStatus.DISABLED;
        const isOrgRoleDisabled =
          organizationRoleStatus === UserOrganizationRoleStatus.DISABLED;

        if ((isUserDisabled || isOrgRoleDisabled) && !hasCheckedForSwitch) {
          setHasCheckedForSwitch(true);

          // Try to get user data with organization to check for auto-switch
          try {
            const freshData = await userService.getSelfWithOrganization();

            if (freshData.organization && freshData.organization.organizationId !== organizationId) {
              // Backend switched to a different organization, update Redux and storage
              dispatch(setOrganizationData(freshData.organization));
              dispatch(setUserDetails({
                role: freshData.user.role,
                status: freshData.user.status,
                isReadOnly: false,
              }));
              setOrganizationIdLocalStorage(freshData.organization.organizationId);
              setOrganizationIdSessionStorage(freshData.organization.organizationId);

              // Redirect to home to load the new organization
              if (location.pathname === '/user-disabled') {
                history.push('/');
              } else {
                window.location.reload();
              }
              return;
            }
          } catch (error) {
            console.error('Error checking for organization switch:', error);
          }

          // No switch happened, redirect to user-disabled
          if (location.pathname !== '/user-disabled') {
            history.push('/user-disabled');
          }
        }
      };

      checkAndHandleDisabledStatus();
    }, [
      currentUserData,
      location.pathname,
      history,
      dispatch,
      reduxStatus,
      reduxRole,
      reduxIsReadOnly,
      organizationId,
      hasCheckedForSwitch,
    ]);

    return null;
  };

  const OrganizationStatusChecker: React.FC = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const location = useLocation();
    const [hasCheckedForActiveOrg, setHasCheckedForActiveOrg] = useState(false);
    const organizationId = useAppSelector(
      (state: RootState) => state.organization.organizationId,
    );
    const { data: organization } = useOrganization(organizationId);

    useEffect(() => {
      const checkAndSwitchOrganization = async () => {
        if (!isLoggedIn || !organization || !organizationId) return;

        const orgStatus = organization.status;

        // Redirect to organization-disabled if status is DISABLED
        if (
          orgStatus === OrganizationStatus.DISABLED &&
          location.pathname !== '/organization-disabled'
        ) {
          history.push('/organization-disabled');
          return;
        }

        // If organization is SUSPENDED, try to find another active organization
        if (orgStatus === OrganizationStatus.SUSPENDED) {
          // Only check once to avoid infinite loops
          if (!hasCheckedForActiveOrg) {
            setHasCheckedForActiveOrg(true);
            try {
              // Import dynamically to avoid circular dependencies
              const userService = (await import('@services/user')).default;
              const { setOrganizationData } = await import(
                '@redux/reducers/organization.reducer'
              );

              // Call backend to get the correct active organization
              const freshData = await userService.getSelfWithOrganization();

              if (
                freshData.organization &&
                freshData.organization.status !== OrganizationStatus.SUSPENDED &&
                freshData.organization.organizationId !== organizationId
              ) {
                // Backend found a different active organization, switch to it
                dispatch(setOrganizationData(freshData.organization));
                // Reload to ensure all data is fresh
                window.location.href = '/';
                return;
              }
            } catch (error) {
              console.error('Error checking for active organization:', error);
            }
          }

          // No active organization found, redirect to suspended page
          if (location.pathname !== '/organization-suspended') {
            history.push('/organization-suspended');
          }
        }
      };

      checkAndSwitchOrganization();
    }, [
      organization,
      location.pathname,
      history,
      organizationId,
      dispatch,
      hasCheckedForActiveOrg,
    ]);

    return null;
  };

  const NoOrganizationChecker: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const [isReady, setIsReady] = useState(false);
    const organizationId = useAppSelector(
      (state: RootState) => state.organization.organizationId,
    );
    const userRole = useAppSelector((state: RootState) => state.user.role);

    // Wait for Redux state to be fully populated after login/rehydration
    // This prevents premature redirects before StatusChecker has a chance to update the role
    useEffect(() => {
      const timer = setTimeout(() => {
        setIsReady(true);
      }, 500);
      return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
      // Don't redirect until we've waited for state to settle
      if (!isReady) return;

      // Only redirect if logged in and (no organization AND no role)
      // This handles users who registered but haven't been assigned to an organization yet
      if (
        isLoggedIn &&
        !organizationId &&
        !userRole &&
        location.pathname !== '/no-organization' &&
        location.pathname !== '/logout' &&
        location.pathname !== '/user-disabled'
      ) {
        history.push('/no-organization');
      }
    }, [organizationId, userRole, location.pathname, history, isReady]);

    return null;
  };

  const MobileWidthChecker: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
      const handleResize = () => {
        const mobile = window.innerWidth <= 768;
        setIsMobile(mobile);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
      if (isMobile && location.pathname !== '/mobile-only') {
        history.push('/mobile-only');
      } else if (!isMobile && location.pathname === '/mobile-only') {
        history.push('/dashboard');
      }
    }, [isMobile, location.pathname, history]);

    return null;
  };

  const AnalyticsPageViewTracker: React.FC = () => {
    const location = useLocation();
    const didInit = useRef(false);

    useEffect(() => {
      if (!didInit.current) {
        initAnalytics();
        didInit.current = true;
      }
      trackPageView(
        sanitizePageLocation(window.location.origin, location.pathname, location.search),
        stripNonUtmQuery(window.location.origin, location.pathname, location.search),
      );
    }, [location.pathname, location.search]);

    return null;
  };

  return (
    <Router>
      <StatusChecker />
      <OrganizationStatusChecker />
      <NoOrganizationChecker />
      <MobileWidthChecker />
      <AnalyticsPageViewTracker />
      <Switch>
        {/* Mobile Only Route */}
        <Route exact path="/mobile-only">
          <MobileOnly />
        </Route>

        {/* User Disabled Route */}
        <Route exact path="/user-disabled">
          <UserDisabled />
        </Route>

        {/* Organization Disabled Route */}
        <Route exact path="/organization-disabled">
          <OrganizationDisabled />
        </Route>

        {/* Organization Suspended Route */}
        <Route exact path="/organization-suspended">
          {isLoggedIn ? <OrganizationSuspended /> : <Redirect to="/login" />}
        </Route>

        {/* No Organization Route */}
        <Route exact path="/no-organization">
          {isLoggedIn ? <NoOrganization /> : <Redirect to="/login" />}
        </Route>

        <Route path="/login">
          {isLoggedIn ? (
            <Redirect to={getPostLoginRedirect(role)} />
          ) : (
            <LoginPage />
          )}
        </Route>

        <Route path="/register">
          {isLoggedIn ? (
            <Redirect to={getPostLoginRedirect(role)} />
          ) : (
            <RegisterPage />
          )}
        </Route>

        {/* Redirect from /auth/code... to /dashboard if logged in */}
        <Route path="/auth/*">
          {isLoggedIn ? (
            <Redirect to={getPostLoginRedirect(role)} />
          ) : (
            <AuthPage />
          )}
        </Route>

        {/* Specific route for the authentication page */}
        <Route exact path="/auth">
          {isLoggedIn ? (
            <Redirect to={getPostLoginRedirect(role)} />
          ) : (
            <AuthPage />
          )}
        </Route>

        <Route exact path="/logout">
          <LogoutPage />
        </Route>

        {/* Sprint 39, story-212: friendly aliases. Must precede the private block and the
            catch-alls. The reset alias keeps the query string verbatim: login.tsx opens the
            reset view for ?code= and the invitation deep link for ?code=&user=. */}
        <Redirect exact from="/signup" to="/register" />
        <Redirect exact from="/create-account" to="/register" />
        <Redirect
          exact
          from="/forgot-password"
          to="/login?action=forgot-password"
        />
        <Route
          exact
          path="/reset-password"
          render={({ location }) => (
            <Redirect to={{ pathname: '/login', search: location.search }} />
          )}
        />

        {/* PrivateRoute for Dashboard and all other authenticated routes */}
        {[UserRoles.SUPER_ADMIN].includes(role as UserRoles) && (
          <PrivateRoute path="/organizations" component={OrganizationsPage} />
        )}
        {[UserRoles.SUPER_ADMIN].includes(role as UserRoles) && (
          <PrivateRoute
            path="/subscription-plans"
            component={SubscriptionPlansAdminPage}
          />
        )}
        {[UserRoles.SUPER_ADMIN].includes(role as UserRoles) && (
          <PrivateRoute path="/user-admin" component={AdminPage} />
        )}
        {[UserRoles.SUPER_ADMIN].includes(role as UserRoles) && (
          <PrivateRoute path="/admin-billing" component={AdminBillingPage} />
        )}
        {[UserRoles.SUPER_ADMIN].includes(role as UserRoles) && (
          <PrivateRoute exact path="/admin/warehouse/categories" component={SuperAdminCategoriesPage} />
        )}
        {[UserRoles.SUPER_ADMIN].includes(role as UserRoles) && (
          <PrivateRoute
            exact
            path="/admin/platform-status"
            component={PlatformStatusAdminPage}
          />
        )}
        {[UserRoles.SUPER_ADMIN].includes(role as UserRoles) && (
          <PrivateRoute
            exact
            path="/admin/notification-recipients"
            component={SuperAdminNotificationRecipientsPage}
          />
        )}
        {canAccess('STANDALONE_INVOICE', role) && (
          <PrivateRoute exact path="/admin/invoices/new" component={StandaloneInvoicePage} />
        )}
        {canAccess('FIELD_CAPTURE_ADMIN', role) && (
          <PrivateRoute path="/admin/field-capture" component={FieldCaptureAdminPage} />
        )}
        {[UserRoles.SUPER_ADMIN].includes(role as UserRoles) && (
          <PrivateRoute path="/settings" component={SettingsPage} />
        )}
        {isMobileOnlyRole(role) && (
          <PrivateHomeRoute path="/home" component={HomePage} />
        )}
        <PrivateRoute exact path="/dashboard" component={DashboardPage} />

        {/* Paywall for a lapsed card-free trial. Inside the normal Layout on purpose —
            the organization is read-only, not locked out, so the shell stays navigable. */}
        <PrivateRoute exact path="/trial-expired" component={TrialExpiredPage} />
        <PrivateRoute exact path="/mapView" component={MapViewPage} />
        <PrivateRoute exact path="/ui" component={UiComponents} />
        <PrivateRoute path="/settings" component={SettingsPage} />
        <PrivateRoute path="/profile" component={ProfilePage} />
        {/* Sprint 37, story-196. One Route for `/assistant` and `/assistant/:conversationId` so the page
            instance survives the URL replace after the first send. Same role gate as the nav item. */}
        {canAccess('ASSISTANT', role) && (
          <PrivateRoute path="/assistant/:conversationId?" component={AssistantPage} />
        )}
        <PrivateRoute exact path="/reports/daily" component={DailySiteReportsPage} />
        <PrivateRoute exact path="/reports/daily/:reportId" component={DailySiteReportDetailPage} />
        {role === UserRoles.OWNER && (
          <PrivateRoute path="/billing" component={OwnerBillingPage} />
        )}
        {canAccess('USERS', role) && (
          <PrivateRoute path="/users" component={UsersPage} />
        )}
        {/* Sprint 31, story-160/161. Inserted above `/schedule/:id/:taskId` so `/projects/new`
            and `/projects/:id/plan/ai` don't get swallowed by `/projects/:id?` below. */}
        <PrivateRoute exact path="/projects/new" component={ProjectWizardPage} />
        <PrivateRoute exact path="/projects/:id/plan/ai" component={AiPlanPage} />
        {canAccess('TEMPLATES', role) && (
          <PrivateRoute exact path="/templates/generate" component={AiPlanPage} />
        )}
        {/* Second segment is the task to open — it was named `:status` and
            nothing ever read it, so "View in Project" silently dropped it. */}
        <PrivateRoute path="/schedule/:id/:taskId" component={SchedulePage} />
        <PrivateRoute path="/schedule/:id" component={SchedulePage} />
        <PrivateRoute path="/projects/:id?" component={ProjectsPage} />
        <PrivateRoute path="/clients/:id?" component={ClientsPage} />
        <PrivateRoute path="/schedule" component={SchedulePage} />
        <PrivateRoute path="/crews/:id" component={CrewsPage} />
        <PrivateRoute path="/crews" component={CrewsPage} />
        <PrivateRoute
          path="/subcontractors/:id"
          component={SubcontractorsPage}
        />
        <PrivateRoute path="/subcontractors" component={SubcontractorsPage} />
        <PrivateRoute path="/budgets" component={BudgetsPage} />
        {canAccess('INVOICES', role) && (
          <PrivateRoute path="/invoices" component={InvoicesPage} />
        )}
        <PrivateRoute path="/documents" component={DocumentsPage} />
        {canAccess('TEMPLATES', role) && (
          <PrivateRoute path="/templates" component={TemplatesPage} />
        )}
        <PrivateRoute path="/incidents/:id?" component={IncidentsPage} />
        <PrivateRoute exact path="/warehouse/categories" component={WarehouseCategoriesPage} />
        <PrivateRoute exact path="/warehouse/reports/valuation" component={InventoryValuationReportPage} />
        <PrivateRoute exact path="/warehouse/equipment/:id" component={EquipmentAssetDetailPage} />
        <PrivateRoute exact path="/warehouse/equipment" component={EquipmentAssetsPage} />
        <PrivateRoute exact path="/warehouse/alerts" component={LowStockAlertsPage} />
        <PrivateRoute exact path="/warehouse/transactions" component={TransactionsPage} />
        <PrivateRoute exact path="/warehouse/materials" component={MaterialCatalogPage} />
        <PrivateRoute exact path="/warehouse/manage" component={WarehouseManagementPage} />
        <PrivateRoute exact path="/warehouse" component={WarehousePage} />
        <PrivateRoute exact path="/geofences/categories" component={GeofenceCategoriesPage} />
        <PrivateRoute exact path="/geofences" component={GeofencesPage} />

        {/* Sprint 39, story-212: three routes replace the old catch-all that rendered the
            login page at ANY url. Order is load-bearing. */}
        <Route exact path="/">
          {isLoggedIn ? (
            <Redirect to={getPostLoginRedirect(role)} />
          ) : (
            <LoginPage />
          )}
        </Route>

        {/* Known app paths that matched no route above: role-gated routes are not mounted for
            this user. Logged out: remember the path and go to login. Logged in: exactly what the
            old catch-all did (land on the role's home). */}
        <Route path={KNOWN_APP_PATH_PREFIXES}>
          {isLoggedIn ? (
            <Redirect to={getPostLoginRedirect(role)} />
          ) : (
            <KnownPathRedirect />
          )}
        </Route>

        <Route>
          <NotFoundPage />
        </Route>
      </Switch>
    </Router>
  );
};

export default App;
